import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Gauge, ArrowsClockwise, DeviceMobile, Desktop, Warning, CheckCircle, XCircle, ShieldCheck, LinkBreak, Sparkle, TrendUp, Lightbulb } from "@phosphor-icons/react";
import { fmtNum } from "./format";
import { SkeletonCard } from "./Skeleton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Grade → colour tokens */
const GRADE_COLOR = {
  good: "text-emerald-300",
  needs_improvement: "text-yellow-300",
  poor: "text-rose-300",
  unknown: "text-white/40",
};
const GRADE_BG = {
  good: "bg-emerald-500/15",
  needs_improvement: "bg-yellow-500/15",
  poor: "bg-rose-500/15",
  unknown: "bg-white/[0.02]",
};

const _trim = (u) => (u || "").replace(/^https?:\/\/[^/]+/, "") || "/";

const _fmtMetric = (m, unit) => {
  if (!m || m.value == null) return "—";
  if (unit === "ms") return `${fmtNum(m.value)}\u202Fms`;
  return `${m.value}`;
};

/* ------- AI Technical Brief block ------- */
const AiTechnicalBrief = ({ brief }) => {
  if (!brief || !brief.diagnosis) return null;
  const critical = brief.critical_fixes || [];
  const wins = brief.wins || [];
  const quickWins = brief.quick_wins || [];
  return (
    <div className="rounded-lg border border-[#A78BFA22] bg-gradient-to-r from-[#A78BFA11] to-transparent p-4 space-y-3" data-testid="tech-seo-ai-brief">
      <div className="flex items-center gap-2">
        <Sparkle size={14} className="text-[#A78BFA]" />
        <span className="text-[10px] uppercase tracking-wider text-white/55">AI Technical Diagnosis</span>
      </div>
      <p className="text-sm text-white/90 leading-relaxed">{brief.diagnosis}</p>

      {critical.length > 0 && (
        <div>
          <div className="text-[11px] font-medium text-rose-300 mb-1.5 flex items-center gap-1">
            <XCircle size={12} /> Critical fixes ({critical.length})
          </div>
          <div className="space-y-2">
            {critical.slice(0, 5).map((f, i) => (
              <div key={i} className="rounded border border-white/10 bg-white/[0.03] p-2.5">
                <div className="flex items-start gap-2">
                  <span className={`text-[9px] px-1.5 py-0.5 rounded font-semibold shrink-0 ${
                    f.priority === "P0" ? "bg-red-500/20 text-red-300"
                    : f.priority === "P1" ? "bg-yellow-500/20 text-yellow-300"
                    : "bg-white/10 text-white/70"
                  }`}>{f.priority || "P2"}</span>
                  {f.pillar && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#A78BFA]/20 text-[#A78BFA] font-semibold capitalize shrink-0">{f.pillar}</span>
                  )}
                  <div className="flex-1">
                    <div className="text-xs text-white/95 font-medium">{f.title}</div>
                    {f.why && <div className="text-[11px] text-white/60 mt-0.5"><span className="uppercase text-[9px] text-white/40">Why:</span> {f.why}</div>}
                    {f.how && <div className="text-[11px] text-white/75 mt-0.5"><span className="uppercase text-[9px] text-white/40">How:</span> {f.how}</div>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {quickWins.length > 0 && (
          <div>
            <div className="text-[11px] font-medium text-yellow-300 mb-1 flex items-center gap-1">
              <Lightbulb size={12} /> Quick wins
            </div>
            <ul className="space-y-0.5 text-xs text-white/75 list-disc list-inside">
              {quickWins.slice(0, 5).map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>
        )}
        {wins.length > 0 && (
          <div>
            <div className="text-[11px] font-medium text-emerald-300 mb-1 flex items-center gap-1">
              <CheckCircle size={12} /> What&apos;s working
            </div>
            <ul className="space-y-0.5 text-xs text-white/75 list-disc list-inside">
              {wins.slice(0, 5).map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>
        )}
      </div>

      {brief.next_action && (
        <div className="border-t border-white/10 pt-3 text-xs">
          <span className="text-white/55 uppercase tracking-wider text-[10px]">Next action · </span>
          <span className="text-white/90">{brief.next_action}</span>
        </div>
      )}
    </div>
  );
};

/* ------- Score ring ------- */
const ScoreRing = ({ score, size = 100, grade }) => {
  const radius = (size - 12) / 2;
  const circ = 2 * Math.PI * radius;
  const dash = score != null ? (score / 100) * circ : 0;
  const color = grade === "excellent" ? "#10B981"
    : grade === "good" ? "#3B82F6"
    : grade === "needs_work" ? "#F59E0B"
    : grade === "poor" ? "#EF4444" : "#71717A";
  return (
    <svg width={size} height={size} className="shrink-0" data-testid="tech-seo-score-ring">
      <circle cx={size/2} cy={size/2} r={radius} stroke="#ffffff10" strokeWidth={8} fill="none" />
      <circle
        cx={size/2} cy={size/2} r={radius}
        stroke={color} strokeWidth={8} fill="none"
        strokeLinecap="round"
        strokeDasharray={`${dash} ${circ}`}
        transform={`rotate(-90 ${size/2} ${size/2})`}
        style={{ transition: "stroke-dasharray 0.6s ease" }}
      />
      <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central"
            fill="#fff" fontSize={size * 0.32} fontWeight="600"
            fontFamily="Varela Round, sans-serif">
        {score != null ? score : "—"}
      </text>
    </svg>
  );
};

/* ------- CWV metric card ------- */
const CwvCard = ({ label, metric, unit, description }) => {
  const grade = metric?.grade || "unknown";
  return (
    <div className={`rounded-lg border border-white/10 p-3 ${GRADE_BG[grade]}`} data-testid={`tech-seo-cwv-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-white/50">{label}</div>
        {grade === "good" && <CheckCircle size={12} className="text-emerald-300" />}
        {grade === "needs_improvement" && <Warning size={12} className="text-yellow-300" />}
        {grade === "poor" && <XCircle size={12} className="text-rose-300" />}
      </div>
      <div className={`text-lg font-semibold mt-1 ${GRADE_COLOR[grade]}`} style={{ fontFamily: "Varela Round, sans-serif" }}>
        {_fmtMetric(metric, unit)}
      </div>
      <div className="text-[10px] text-white/45 mt-0.5">{description}</div>
      {metric?.sample_size && (
        <div className="text-[9px] text-white/30 mt-0.5">Avg across {metric.sample_size} pages</div>
      )}
    </div>
  );
};

/* ------- Component score row ------- */
const ComponentRow = ({ label, component, icon: Icon }) => {
  const score = component?.score;
  const detail = component?.detail;
  const barColor = score == null ? "#71717A"
    : score >= 85 ? "#10B981"
    : score >= 70 ? "#3B82F6"
    : score >= 50 ? "#F59E0B" : "#EF4444";
  return (
    <div className="flex items-center gap-3 py-1.5" data-testid={`tech-seo-component-${label.toLowerCase()}`} title={detail || ""}>
      <div className="flex items-center gap-1.5 w-28 text-xs text-white/80 shrink-0">
        {Icon && <Icon size={12} className="text-white/60" />}
        {label}
      </div>
      <div className="flex-1 h-2 bg-white/[0.04] rounded overflow-hidden">
        <div
          className="h-full transition-all duration-500"
          style={{ width: score != null ? `${Math.max(2, score)}%` : "0%", background: barColor }}
        />
      </div>
      <div className={`w-16 text-xs text-right font-semibold ${score == null ? "text-white/40" : score < 50 ? "text-rose-300" : score < 70 ? "text-yellow-300" : "text-white"}`}>
        {score != null ? `${score}` : "N/A"}
      </div>
    </div>
  );
};

const BucketPill = ({ label, count, tone }) => {
  const toneClass = {
    good: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    warn: "bg-yellow-500/15 text-yellow-300 border-yellow-500/30",
    bad: "bg-rose-500/15 text-rose-300 border-rose-500/30",
    neutral: "bg-white/[0.04] text-white/60 border-white/15",
  }[tone] || "bg-white/[0.04] text-white/60 border-white/15";
  return (
    <div className={`rounded border px-2.5 py-1.5 text-center ${toneClass}`}>
      <div className="text-[10px] uppercase tracking-wider">{label}</div>
      <div className="text-base font-semibold" style={{ fontFamily: "Varela Round, sans-serif" }}>{count}</div>
    </div>
  );
};

const BUCKET_TONE = {
  indexed: "good",
  crawled_not_indexed: "warn",
  discovered_not_indexed: "warn",
  duplicate: "warn",
  canonical_alternate: "neutral",
  redirect: "neutral",
  excluded: "warn",
  not_found: "bad",
  error: "bad",
  unknown_to_google: "bad",
  other: "neutral",
  unknown: "neutral",
};
const BUCKET_LABEL = {
  indexed: "Indexed",
  crawled_not_indexed: "Crawled · not indexed",
  discovered_not_indexed: "Discovered · not indexed",
  duplicate: "Duplicate",
  canonical_alternate: "Alt canonical",
  redirect: "Redirect",
  excluded: "Excluded",
  not_found: "404 / Not found",
  error: "Errors",
  unknown_to_google: "Unknown to Google",
  other: "Other",
};

/* ==================== MAIN SECTION ==================== */

export const TechnicalSeoSection = ({ clientId, dateRange /* unused, kept for API parity */ }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState("");
  const [inspExpanded, setInspExpanded] = useState(false);

  const load = useCallback(async (forceRefresh = false) => {
    const setter = forceRefresh ? setRefreshing : setLoading;
    setter(true); setErr("");
    try {
      const res = await axios.get(`${API}/clients/${clientId}/search-console/technical-seo`, {
        params: forceRefresh ? { force_refresh: true } : {},
      });
      setData(res.data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Failed to load Technical SEO");
    } finally {
      setter(false);
    }
  }, [clientId]);

  useEffect(() => { load(false); }, [load]);

  const bucketCounts = data?.bucket_counts || {};
  const bucketEntries = useMemo(() => Object.entries(bucketCounts).sort((a, b) => b[1] - a[1]), [bucketCounts]);

  if (loading) return <SkeletonCard rows={6} />;
  if (err) {
    return (
      <div className="rounded-lg border border-rose-500/30 bg-rose-500/[0.05] p-4 text-xs text-rose-200" data-testid="tech-seo-err">
        Technical SEO — {err}
      </div>
    );
  }
  if (!data) return null;

  const cwvMobile = data.cwv_summary?.mobile || {};
  const overall = data.overall || {};

  return (
    <div className="space-y-4" data-testid="tech-seo-section">
      {/* Header + Score */}
      <div className="rounded-lg border border-white/10 bg-white/[0.02] p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <ScoreRing score={overall.score} grade={overall.grade} />
            <div>
              <div className="text-[10px] uppercase tracking-wider text-white/50 flex items-center gap-1.5">
                <Gauge size={12} /> Technical SEO Score
              </div>
              <div className="text-2xl font-semibold text-white mt-0.5" style={{ fontFamily: "Varela Round, sans-serif" }}>
                {overall.score != null ? `${overall.score}/100` : "Not enough data"}
              </div>
              <div className="text-xs text-white/60 capitalize mt-0.5">
                {overall.grade === "excellent" && "✨ Excellent — top-tier technical health"}
                {overall.grade === "good" && "Good — a few pillars to tune"}
                {overall.grade === "needs_work" && "Needs work — several critical fixes"}
                {overall.grade === "poor" && "Poor — urgent technical debt"}
                {overall.grade === "unknown" && "Insufficient data"}
              </div>
              {data.generated_at && (
                <div className="text-[10px] text-white/35 mt-1">
                  {data.cached ? "Cached · " : ""}Generated {new Date(data.generated_at).toLocaleString()} · analyzed {data.pages_analyzed} pages
                </div>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={() => load(true)}
            disabled={refreshing}
            className="flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded border border-white/15 text-white/70 hover:bg-white/[0.04] disabled:opacity-50"
            data-testid="tech-seo-refresh"
          >
            <ArrowsClockwise size={12} className={refreshing ? "animate-spin" : ""} />
            {refreshing ? "Rescanning…" : "Rescan"}
          </button>
        </div>

        {/* Component breakdown */}
        <div className="mt-4 border-t border-white/10 pt-3">
          <div className="text-[10px] uppercase tracking-wider text-white/50 mb-2">Pillars</div>
          <div className="space-y-1">
            <ComponentRow label="Speed" component={data.components?.speed} icon={Gauge} />
            <ComponentRow label="Indexation" component={data.components?.indexation} icon={CheckCircle} />
            <ComponentRow label="Mobile" component={data.components?.mobile} icon={DeviceMobile} />
            <ComponentRow label="Sitemaps" component={data.components?.sitemap} icon={CheckCircle} />
            <ComponentRow label="Security" component={data.components?.security} icon={ShieldCheck} />
          </div>
          <div className="mt-2 space-y-0.5 text-[10px] text-white/45">
            {Object.entries(data.components || {}).map(([k, v]) => v?.detail && (
              <div key={k}><span className="capitalize text-white/60">{k}:</span> {v.detail}</div>
            ))}
          </div>
        </div>
      </div>

      {/* NEW: AI Technical Brief */}
      <AiTechnicalBrief brief={data.ai_brief} />

      {/* Core Web Vitals — Mobile field data */}
      {!data.pagespeed_configured ? (
        <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/[0.05] p-4" data-testid="tech-seo-pagespeed-not-configured">
          <div className="flex items-start gap-2">
            <Warning size={16} className="text-yellow-300 shrink-0 mt-0.5" />
            <div>
              <div className="text-sm text-white/90 font-medium">Core Web Vitals not configured</div>
              <div className="text-xs text-white/60 mt-1">
                To fetch LCP / INP / CLS from PageSpeed Insights, a super-admin needs to add <code className="px-1 py-0.5 rounded bg-white/[0.06] text-white/80">PAGESPEED_API_KEY</code> to the backend env.
                <br />
                Free from{" "}
                <a href="https://console.cloud.google.com/apis/credentials?project=487361943295" target="_blank" rel="noreferrer" className="underline text-yellow-300">
                  Google Cloud Console → Credentials → Create API Key
                </a>
                {" "}(enable the PageSpeed Insights API first). Restrict the key to that single API for safety. 25,000 queries/day free.
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4">
          <div className="flex items-center gap-2 mb-3">
            <DeviceMobile size={14} className="text-[#4285F4]" />
            <span className="text-[10px] uppercase tracking-wider text-white/50">Core Web Vitals · Mobile (real-user field data)</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <CwvCard label="LCP" metric={cwvMobile.LCP} unit="ms" description="Largest Contentful Paint. Good ≤ 2.5s" />
            <CwvCard label="INP" metric={cwvMobile.INP} unit="ms" description="Interaction to Next Paint. Good ≤ 200ms" />
            <CwvCard label="CLS" metric={cwvMobile.CLS} unit="" description="Cumulative Layout Shift. Good ≤ 0.1" />
          </div>
          {!cwvMobile.LCP && !cwvMobile.INP && !cwvMobile.CLS && (
            <div className="text-[10px] text-white/45 mt-2 italic">
              No CrUX field data yet — the site may not have enough real-user traffic. Lab (Lighthouse) data still contributes to the overall score.
            </div>
          )}
        </div>
      )}

      {/* Per-URL PageSpeed table */}
      {data.pagespeed_runs && data.pagespeed_runs.length > 0 && (
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4">
          <div className="text-[10px] uppercase tracking-wider text-white/50 mb-2">
            PageSpeed per URL · mobile & desktop performance score
          </div>
          <div className="overflow-x-auto rounded border border-white/10">
            <table className="w-full text-xs">
              <thead className="bg-white/[0.03] text-white/55 uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="text-left px-3 py-2">URL</th>
                  <th className="text-center px-3 py-2"><DeviceMobile size={11} className="inline" /> Mobile</th>
                  <th className="text-center px-3 py-2"><Desktop size={11} className="inline" /> Desktop</th>
                  <th className="text-right px-3 py-2">LCP (lab)</th>
                  <th className="text-right px-3 py-2">CLS (lab)</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Group runs by URL, pair mobile/desktop
                  const grouped = {};
                  data.pagespeed_runs.forEach(r => {
                    if (!grouped[r.url]) grouped[r.url] = {};
                    grouped[r.url][r.strategy] = r;
                  });
                  return Object.entries(grouped).map(([url, s]) => {
                    const mob = s.mobile || {};
                    const desk = s.desktop || {};
                    const lcp = (mob.lab?.LCP || desk.lab?.LCP) || {};
                    const cls = (mob.lab?.CLS || desk.lab?.CLS) || {};
                    const scoreClass = (n) => n == null ? "text-white/40"
                      : n >= 90 ? "text-emerald-300"
                      : n >= 50 ? "text-yellow-300" : "text-rose-300";
                    return (
                      <tr key={url} className="border-t border-white/5 hover:bg-white/[0.02]">
                        <td className="px-3 py-2 text-white/85 truncate max-w-[300px]" title={url}>
                          <a href={url} target="_blank" rel="noreferrer" className="hover:text-[#FFEB00]">{_trim(url)}</a>
                        </td>
                        <td className={`px-3 py-2 text-center font-semibold ${scoreClass(mob.performance_score)}`}>
                          {mob.performance_score != null ? mob.performance_score : "—"}
                        </td>
                        <td className={`px-3 py-2 text-center font-semibold ${scoreClass(desk.performance_score)}`}>
                          {desk.performance_score != null ? desk.performance_score : "—"}
                        </td>
                        <td className={`px-3 py-2 text-right ${GRADE_COLOR[lcp.grade || "unknown"]}`}>
                          {lcp.value != null ? `${fmtNum(lcp.value)} ms` : "—"}
                        </td>
                        <td className={`px-3 py-2 text-right ${GRADE_COLOR[cls.grade || "unknown"]}`}>
                          {cls.value != null ? cls.value.toFixed(3) : "—"}
                        </td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Indexation Health */}
      <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-wider text-white/50">
            Indexation Health · {data.pages_analyzed} URLs inspected
          </div>
          {(bucketCounts.not_found || bucketCounts.error) && (
            <span className="text-[10px] px-2 py-0.5 rounded bg-rose-500/20 text-rose-300 font-semibold">
              {(bucketCounts.not_found || 0) + (bucketCounts.error || 0)} issue{(bucketCounts.not_found || 0) + (bucketCounts.error || 0) !== 1 ? "s" : ""}
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
          {bucketEntries.map(([bucket, count]) => (
            <BucketPill
              key={bucket}
              label={BUCKET_LABEL[bucket] || bucket}
              count={count}
              tone={BUCKET_TONE[bucket] || "neutral"}
            />
          ))}
        </div>

        {/* Canonical mismatches */}
        {data.canonical_mismatches && data.canonical_mismatches.length > 0 && (
          <div className="mt-3 border-t border-white/10 pt-3">
            <div className="text-[10px] font-medium text-yellow-300 mb-1.5 flex items-center gap-1">
              <LinkBreak size={11} /> Canonical mismatches ({data.canonical_mismatches.length})
              <span className="text-white/40 font-normal ml-1">— Google chose a different canonical than you specified</span>
            </div>
            <div className="space-y-1">
              {data.canonical_mismatches.slice(0, 5).map(cm => (
                <div key={cm.url} className="text-[11px] py-1 border-b border-white/5 last:border-b-0">
                  <a href={cm.url} target="_blank" rel="noreferrer" className="text-white/80 hover:text-[#FFEB00] block truncate" title={cm.url}>{_trim(cm.url)}</a>
                  <div className="text-[10px] text-white/45 pl-3">Google picked: <span className="text-white/65">{_trim(cm.google_canonical)}</span></div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Rich results detected */}
        {data.rich_results_summary && data.rich_results_summary.length > 0 && (
          <div className="mt-3 border-t border-white/10 pt-3">
            <div className="text-[10px] uppercase tracking-wider text-white/50 mb-1.5">Rich-result markup detected</div>
            <div className="flex flex-wrap gap-1.5">
              {data.rich_results_summary.map(r => (
                <span key={r.type} className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                  {r.type} · {r.pages_count} page{r.pages_count > 1 ? "s" : ""}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Toggle: full inspection table */}
        <div className="mt-3 border-t border-white/10 pt-3">
          <button
            type="button"
            onClick={() => setInspExpanded(!inspExpanded)}
            className="text-[11px] text-white/60 hover:text-white/85"
            data-testid="tech-seo-toggle-inspections"
          >
            {inspExpanded ? "▲ Hide" : "▼ Show"} full URL inspection table
          </button>
          {inspExpanded && (
            <div className="overflow-x-auto rounded border border-white/10 mt-2">
              <table className="w-full text-xs">
                <thead className="bg-white/[0.03] text-white/55 uppercase tracking-wider text-[10px]">
                  <tr>
                    <th className="text-left px-3 py-2">URL</th>
                    <th className="text-left px-3 py-2">Coverage state</th>
                    <th className="text-left px-3 py-2">Verdict</th>
                    <th className="text-left px-3 py-2">Mobile</th>
                    <th className="text-left px-3 py-2">Last crawl</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.inspections || []).map(i => (
                    <tr key={i.url} className="border-t border-white/5 hover:bg-white/[0.02]">
                      <td className="px-3 py-2 text-white/85 truncate max-w-[280px]" title={i.url}>
                        <a href={i.url} target="_blank" rel="noreferrer" className="hover:text-[#FFEB00]">{_trim(i.url)}</a>
                      </td>
                      <td className={`px-3 py-2 text-white/80 text-[11px]`}>{i.coverage_state || "—"}</td>
                      <td className="px-3 py-2">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                          i.verdict === "PASS" ? "bg-emerald-500/20 text-emerald-300"
                            : i.verdict === "PARTIAL" ? "bg-yellow-500/20 text-yellow-300"
                            : i.verdict === "FAIL" ? "bg-rose-500/20 text-rose-300"
                            : "bg-white/10 text-white/60"
                        }`}>{i.verdict || "—"}</span>
                      </td>
                      <td className="px-3 py-2 text-[11px] text-white/70">{i.mobile_verdict || "—"}</td>
                      <td className="px-3 py-2 text-[10px] text-white/55">
                        {i.last_crawl_time ? new Date(i.last_crawl_time).toLocaleDateString() : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="text-[10px] text-white/40 text-right">
        PageSpeed data cached 24h · URL inspection cached 12h · Click Rescan to force-refresh.
      </div>
    </div>
  );
};
